// i18n
import "./locales/i18n";

// scroll bar
import "simplebar-react/dist/simplebar.min.css";

// lightbox
/* eslint-disable import/no-unresolved */
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/styles.css";

// editor
import "react-quill-new/dist/quill.snow.css";

// lazy image
import "react-lazy-load-image-component/src/effects/blur.css";
import "src/sections/@dashboard/distributor-notices/create/style.css";
import "./sections/@dashboard/self-rica/details/style.css";

// react-query
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "src/queryClient";

// ----------------------------------------------------------------------
import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { HelmetProvider } from "react-helmet-async";
import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/lib/integration/react";
// @mui
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
// redux
import { Box, Button, Stack, Typography } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";
import { LicenseInfo } from "@mui/x-license-pro";
import { useEffect, useState } from "react";
import { persistor, store } from "./redux/store";
// routes
import Router from "./routes";
// theme
import ThemeProvider from "./theme";
// locales
import ThemeLocalization from "./locales";
// components
import { AuthProvider } from "./auth/JwtContext";
import { MotionLazyContainer } from "./components/animate";
import { StyledChart } from "./components/chart";
import { ErrorBoundary } from "./components/error-boundary";
import ScrollToTop from "./components/scroll-to-top";
import { SettingsProvider, ThemeSettings } from "./components/settings";
import SnackbarProvider from "./components/snackbar";
import { CorrelationProvider } from "./contexts/CorrelationContext";
import logger from "./utils/logger";

// @mui
// ------------ MUI X ------------
import SPARTermsAndConditionsModal from "./pages/dashboard/SPARTermsandConditionsModal";
import TermsAndConditionsModal from "./pages/dashboard/TermsandConditionsModal";

const MUI_LICENCE_KEY = import.meta.env.VITE_MUI_X_LICENCE_KEY;

LicenseInfo.setLicenseKey(MUI_LICENCE_KEY);

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "megsapp-portal.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "megsapp-portal",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "megsapp-portal.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "663831847499",
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase only if required config is present
let app = null;
let analytics = null;

if (firebaseConfig.apiKey && firebaseConfig.appId) {
  try {
    app = initializeApp(firebaseConfig);
    analytics = getAnalytics(app);
  } catch (error) {
    console.warn("Firebase initialization failed:", error);
  }
}

// ----------------------------------------------------------------------

// App-level error fallback - defined outside component to avoid recreation
function AppErrorFallback() {
  return <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      p: 3,
    }}
  >
    <Stack alignItems="center" spacing={2} sx={{ textAlign: "center", maxWidth: 480 }}>
      <ErrorOutline sx={{ fontSize: 64, color: "error.main", opacity: 0.8 }} />
      <Typography variant="h6">Something went wrong</Typography>
      <Typography variant="body2" color="text.secondary">
        An unexpected error occurred. Please reload the page to continue.
      </Typography>
      <Button
        variant="contained"
        startIcon={<Refresh />}
        onClick={() => window.location.reload()}
        sx={{ mt: 2 }}
      >
        Reload Page
      </Button>
    </Stack>
  </Box>
}

export default function App() {
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [sparTermsModalOpen, setSparTermsModalOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("terms_and_conditions_date") === "null") {
      setTermsModalOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!termsModalOpen && localStorage.getItem("spar_terms_and_conditions_date") === "null") {
      setSparTermsModalOpen(true);
    }
  }, [termsModalOpen]);

  useEffect(() => {
    const handleServiceWorkerError = () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.ready
          .then((registration) => {
            registration.unregister();
          })
          .then(() => {
            window.location.reload();
          })
          .catch((error) => {
            logger.error("Error during service worker cleanup", error);
          });
      }
    };

    const handleUnhandledRejection = (event) => {
      if (event.reason && event.reason.message && event.reason.message.includes("Failed to register a ServiceWorker")) {
        handleServiceWorkerError();
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  return (
    <HelmetProvider>
      <ReduxProvider store={store}>
        <PersistGate
          loading={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
              <Typography variant="body2" color="text.secondary">Loading...</Typography>
            </Box>
          }
          persistor={persistor}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <SettingsProvider>
              <BrowserRouter>
                <AuthProvider>
                  <ScrollToTop />
                  <MotionLazyContainer>
                    <ThemeProvider>
                      <ThemeSettings>
                        <ThemeLocalization>
                          <CorrelationProvider>
                            <SnackbarProvider>
                              <StyledChart />
                              <QueryClientProvider client={queryClient}>
                                <ErrorBoundary name="AppRoot" fallback={AppErrorFallback}>
                                  <Router />
                                  <TermsAndConditionsModal open={termsModalOpen} handleClose={() => setTermsModalOpen(false)} />
                                  <SPARTermsAndConditionsModal open={sparTermsModalOpen} handleClose={() => setSparTermsModalOpen(false)} />
                                </ErrorBoundary>
                              </QueryClientProvider>
                            </SnackbarProvider>
                          </CorrelationProvider>
                        </ThemeLocalization>
                      </ThemeSettings>
                    </ThemeProvider>
                  </MotionLazyContainer>
                </AuthProvider>
              </BrowserRouter>
            </SettingsProvider>
          </LocalizationProvider>
        </PersistGate>
      </ReduxProvider>
    </HelmetProvider>
  );
}
